<template>
  <div>
    <div>
      <div>
        <div class="form-group web-hook-container">
          <div class="webhook-label">
            <label>Endpoint: </label>
          </div>
          <div class="d-flex align-items-center webhook-input">
            <b-form-input
              type="text"
              class="form-control"
              v-model.trim="$v.localValue.endpoint.$model"
              :state="validateState('endpoint')"
              placeholder="https://yourendpoint.com"
            />

            <v-btn icon @click.prevent="submit">
              <v-icon size="30" color="green darken-2">fa fa-save</v-icon>
            </v-btn>

            <v-btn icon @click.prevent="removeWebhook">
              <v-icon size="30" color="red darken-2">fa fa-trash</v-icon>
            </v-btn>
          </div>

          <div v-if="!$v.localValue.endpoint.required">
            <p style="color:red;">The endpoint is required</p>
          </div>
        </div>
      </div>

      <div v-if="localValue.secret">
        <template v-if="showSecret">
          <div class="webhook-label">
            <label>Secret Key:</label>
          </div>
          <div class="d-flex align-items-center webhook-input">
            <b-form-input
              class="form-control"
              v-model="localValue.secret"
              readonly
            />

            <div></div>

            <v-btn icon @click="showSecret = false" class="pl-2">
              <v-icon size="30" color="red darken-2">fa fa-eye-slash</v-icon>
            </v-btn>
          </div>
        </template>
        <b-btn
          class="webhook-btn"
          block
          variant="primary"
          v-else
          @click="showSecret = true"
        >
          Show Endpoint Secret Key
        </b-btn>
      </div>
    </div>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      class="webhook-confirmation"
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
  </div>
</template>

<script>
import { required, url } from "vuelidate/lib/validators";
import MerchantSettingsPermissionsMixin from "@/components/merchantsettings/MerchantSettingsPermissionsMixin.js";
import { API, graphqlOperation } from "aws-amplify";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";

const AlertModal = AlertModalMixin.AlertModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

export default {
  mixins: [MerchantSettingsPermissionsMixin, AlertModalMixin],
  props: {
    event: {
      SK: {
        type: String,
        required: true
      },
      Name: {
        type: String,
        required: true
      },
      Description: {
        type: String,
        required: true
      },
      Example: {
        type: Object,
        required: false,
        default() {
          return {};
        }
      }
    },
    value: {
      endpoint: {
        type: String,
        required: true,
        default: ""
      },
      secret: {
        type: String,
        required: true,
        default: ""
      }
    }
  },
  data() {
    return {
      showSecret: false
    };
  },
  validations: {
    localValue: {
      endpoint: {
        required,
        url
      }
    }
  },
  watch: {
    localValue: {
      handler(newVal) {
        this.$emit("input", newVal);
      },
      deep: true
    }
  },
  computed: {
    localValue: {
      get() {
        return this.value;
      }
    },
    eventDisplayName() {
      return this.event.Name;
    },
    eventName() {
      return this.event.SK;
    }
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.localValue[name];
      return $dirty ? !$error : null;
    },
    async saveEndpoint() {
      try {
        const saveMerchantWebhook = `
                mutation SaveMerchantWebhook(
                  $webhook: SaveMerchantWebhookInput!
                ) {
                    saveMerchantWebhook(webhook: $webhook) {
                      endpoint
                      secret
                    }
                }
              `;

        const webhook = {
          webhookEvent: this.eventName,
          endpoint: this.localValue.endpoint
        };

        const saveMerchantWebhookResponse = await API.graphql({
          ...graphqlOperation(saveMerchantWebhook, {
            webhook
          })
        });

        return saveMerchantWebhookResponse.data.saveMerchantWebhook;
      } catch (e) {
        this.$logger.error(e);
        throw new Error("Error saving new webhook endpoint");
      }
    },
    async deleteEndpoint() {
      try {
        const deleteMerchantWebhook = `
                mutation DeleteMerchantWebhook(
                  $webhook: DeleteMerchantWebhookInput!
                ) {
                    deleteMerchantWebhook(webhook: $webhook)
                }
              `;

        const webhook = {
          webhookEvent: this.eventName
        };

        const deleteMerchantWebhookResponse = await API.graphql({
          ...graphqlOperation(deleteMerchantWebhook, {
            webhook
          })
        });

        return deleteMerchantWebhookResponse.data.deleteMerchantWebhook;
      } catch (e) {
        this.$logger.error(e);
        throw new Error("Error deleting webhook endpoint");
      }
    },
    async removeWebhook() {
      if (!this.hasEditMerchantSettingsPermission) return;
      const confirmationTitle = `Remove ${this.eventDisplayName} Endpoint?`;
      const confirmationMessage = `Are you sure you want to remove this webhook?`;
      const confirm = await this.confirm(
        confirmationTitle,
        confirmationMessage,
        ConfirmationModal.WARNING
      );
      if (!confirm) return;
      let saveEndpointResponseMessage = "";
      let alertType = AlertModal.ERROR;
      this.startLoadingDialog(`Removing ${this.eventDisplayName} Endpoint`);
      try {
        const webhookConfig = await this.deleteEndpoint();

        if (!webhookConfig) {
          saveEndpointResponseMessage = `There was an error removing the ${this.eventDisplayName} Endpoint`;
          alertType = AlertModal.ERROR;
        } else {
          this.localValue.endpoint = "";
          this.localValue.secret = "";
          this.showSecret = false;
          saveEndpointResponseMessage = `The ${this.eventDisplayName} has been removed`;
          alertType = AlertModal.SUCCESS;
        }
      } catch (e) {
        this.$logger.error(`Error removing ${this.eventDisplayName} Endpoint`);
        this.$logger.error(e);
        saveEndpointResponseMessage = `An error occurred while removing ${this.eventDisplayName} Endpoint`;
        alertType = AlertModal.ERROR;
      }

      this.stopLoadingDialog(saveEndpointResponseMessage, alertType);
    },
    async submit() {
      if (!this.hasEditMerchantSettingsPermission) return;
      const confirmationTitle = `Save ${this.eventDisplayName} Endpoint?`;
      const confirmationMessage = `Are you sure you want to save this webhook?`;
      const confirm = await this.confirm(
        confirmationTitle,
        confirmationMessage,
        ConfirmationModal.WARNING
      );
      if (!confirm) return;
      let saveEndpointResponseMessage = "";
      let alertType = AlertModal.ERROR;
      this.startLoadingDialog(`Saving ${this.eventDisplayName} Endpoint`);
      try {
        const webhookConfig = await this.saveEndpoint();

        if (!webhookConfig) {
          saveEndpointResponseMessage = `There was an error saving the ${this.eventDisplayName} Endpoint`;
          alertType = AlertModal.ERROR;
        } else {
          this.localValue.endpoint = webhookConfig.endpoint;
          this.localValue.secret = webhookConfig.secret;
          this.showSecret = true;
          saveEndpointResponseMessage = `The ${this.eventDisplayName} has been saved`;
          alertType = AlertModal.SUCCESS;
        }
      } catch (e) {
        this.$logger.error(`Error saving ${this.eventDisplayName} Endpoint`);
        this.$logger.error(e);
        saveEndpointResponseMessage = `An error occurred while saving ${this.eventDisplayName} Endpoint`;
        alertType = AlertModal.ERROR;
      }

      this.stopLoadingDialog(saveEndpointResponseMessage, alertType);
    }
  }
};
</script>

<style lang="scss" scoped>
.web-hook-container {
  display: flex;
  flex-direction: column;
}
.webhook-btn {
  color: #ffff;
  width: 100%;
}
.webhook-label label {
  font-weight: 500 !important;
  font-size: 14px !important;
  margin-bottom: 1rem;
}
</style>
